import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { format } from "date-fns";
import {
  useWebhooks,
  useRetryWebhook,
  type WebhookFilters,
} from "@/hooks/use-webhooks";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Clock, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/webhooks/")({
  component: WebhooksPage,
});
function WebhooksPage() {
  return (
    <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
      <AppShell>
        <WebhooksPageContent />
      </AppShell>
    </ProtectedRoute>
  );
}


function WebhooksPageContent() {
  const [filters, setFilters] = useState<WebhookFilters>({
    page: 1,
    limit: 50,
  });

  const { data: webhooks, isLoading } = useWebhooks(filters);
  const retryMutation = useRetryWebhook();

  const [selectedWebhook, setSelectedWebhook] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleRetry = async (id: string) => {
    try {
      await retryMutation.mutateAsync(id);
      toast.success("Webhook retry triggered");
    } catch (error) {
      toast.error("Failed to retry webhook");
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      sent: "default",
      pending: "secondary",
      failed: "destructive",
    };

    const icons = {
      sent: <CheckCircle2 className="w-3 h-3" />,
      pending: <Clock className="w-3 h-3" />,
      failed: <XCircle className="w-3 h-3" />,
    };

    return (
      <Badge variant={variants[status] || "default"} className="flex items-center gap-1 w-fit">
        {icons[status as keyof typeof icons]}
        {status.toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Webhooks</h1>
          <p className="text-muted-foreground">
            Monitor webhook deliveries and retry failed webhooks
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Status</Label>
              <Select
                value={filters.status || "all"}
                onValueChange={(value) =>
                  setFilters({ ...filters, status: value === "all" ? undefined : value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Event</Label>
              <Select
                value={filters.event || "all"}
                onValueChange={(value) =>
                  setFilters({ ...filters, event: value === "all" ? undefined : value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="order.completed">Order Completed</SelectItem>
                  <SelectItem value="order.failed">Order Failed</SelectItem>
                  <SelectItem value="payout.completed">Payout Completed</SelectItem>
                  <SelectItem value="payout.failed">Payout Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks Table */}
      <Card>
        <CardHeader>
          <CardTitle>Webhook Deliveries</CardTitle>
          <CardDescription>
            {webhooks?.length || 0} webhook deliveries found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : !webhooks || webhooks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No webhooks found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Response</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {webhooks.map((webhook: any) => (
                  <TableRow key={webhook.id}>
                    <TableCell className="font-medium">
                      {webhook.merchant_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{webhook.event}</Badge>
                    </TableCell>
                    <TableCell>
                      <a
                        href={webhook.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline text-sm"
                      >
                        {webhook.url.substring(0, 40)}...
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </TableCell>
                    <TableCell>{getStatusBadge(webhook.status)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {webhook.attempts}/{webhook.max_attempts}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {webhook.last_response_code ? (
                        <Badge
                          variant={
                            webhook.last_response_code >= 200 &&
                            webhook.last_response_code < 300
                              ? "default"
                              : "destructive"
                          }
                        >
                          {webhook.last_response_code}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(webhook.created_at), "PPp")}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedWebhook(webhook);
                            setDetailsOpen(true);
                          }}
                        >
                          Details
                        </Button>
                        {webhook.status === "failed" && (
                          <Button
                            size="sm"
                            onClick={() => handleRetry(webhook.id)}
                            disabled={retryMutation.isPending}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Retry
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Webhook Details</DialogTitle>
            <DialogDescription>
              Complete webhook delivery information
            </DialogDescription>
          </DialogHeader>
          {selectedWebhook && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Webhook ID</Label>
                  <p className="text-sm font-mono">{selectedWebhook.id}</p>
                </div>
                <div>
                  <Label>Order ID</Label>
                  <p className="text-sm font-mono">{selectedWebhook.order_id}</p>
                </div>
                <div>
                  <Label>Merchant</Label>
                  <p className="text-sm">{selectedWebhook.merchant_name}</p>
                </div>
                <div>
                  <Label>Event</Label>
                  <p className="text-sm">{selectedWebhook.event}</p>
                </div>
                <div className="col-span-2">
                  <Label>URL</Label>
                  <p className="text-sm break-all">{selectedWebhook.url}</p>
                </div>
              </div>

              {selectedWebhook.last_error && (
                <div>
                  <Label>Last Error</Label>
                  <pre className="mt-1 p-3 bg-destructive/10 text-destructive rounded-md text-xs overflow-auto">
                    {selectedWebhook.last_error}
                  </pre>
                </div>
              )}

              <div>
                <Label>Payload</Label>
                <pre className="mt-1 p-3 bg-muted rounded-md text-xs overflow-auto max-h-64">
                  {JSON.stringify(selectedWebhook.payload, null, 2)}
                </pre>
              </div>

              {selectedWebhook.next_retry_at && (
                <div>
                  <Label>Next Retry</Label>
                  <p className="text-sm">
                    {format(new Date(selectedWebhook.next_retry_at), "PPp")}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
